import { usePropertyStructureType } from '../../../queries/PropertyFields/TopLevelFields';
import { usePropertyRentalPurchaseDate, usePropertyRentalCurrentDebt } from '../../../queries/PropertyFields/RentalCoreFields';
import {
    usePropertyRentalIsAcquisitionFinancing,
    usePropertyRentalMoreThan10kExpenditures,
} from '../../../queries/PropertyFields/RentalFlagFields';
import { ShowIf } from '../../components/ShowIf';
import { PropertyAddress } from '../components/PropertyAddress/PropertyAddress';
import { YearBuiltRental } from './components/YearBuiltRental';
import { SiteBuiltOrManufacturedRental } from './components/SiteBuiltOrManufacturedRental';
import { AcquisitionFinancingRental } from './components/AcquisitionFinancingRental';
import { CurrentDebtRental } from './components/CurrentDebtRental';
import { LoanProceedPropertyImprovementRental } from './components/LoanProceedPropertyImprovementRental';
import { DateOfPurchase } from './components/DateOfPurchase';
import { TargetLoanRequestRental } from './components/TargetLoanRequestRental';
import { PreferredTerm } from './components/PreferredTerm';
import { StructureAndRentalUnits } from './components/StructureAndRentalUnits';
import { PurchaseContractFile } from '../../components/FileOrFormQuestions/questions/PurchaseContractFile';
import { CapitalExpendituresFile } from '../../components/FileOrFormQuestions/questions/CapitalExpendituresFile';
import { CapitalExpendituresRental } from './components/CapitalExpendituresRental';
import { RentalUnits } from './components/RentalUnits/RentalUnits';
import { ProvideLaterAdvisory } from '../components/ProvideLaterAdvisory';

export interface RentalProps {
    propertyGuid: string;
    isReview?: boolean;
}

export const Rental: React.FC<RentalProps> = ({ propertyGuid, isReview = false }) => {
    const [structureType] = usePropertyStructureType(propertyGuid);
    const [acquisitionToggle] = usePropertyRentalIsAcquisitionFinancing(propertyGuid);
    const [currentDebt] = usePropertyRentalCurrentDebt(propertyGuid);
    const [purchaseDate] = usePropertyRentalPurchaseDate(propertyGuid);
    const [moreThan10kExpenditures] = usePropertyRentalMoreThan10kExpenditures(propertyGuid);

    const isPurchaseDateLessThanTwoYears = (): boolean => {
        if (!purchaseDate) return false;

        const purchaseYearMonthDay = purchaseDate.replace('-', '').replace('-', '');

        const twoYearsBeforeToday = new Date();
        twoYearsBeforeToday.setFullYear(twoYearsBeforeToday.getFullYear() - 2);

        const twoYearsBeforeYearMonthDay = `${twoYearsBeforeToday.getFullYear()}${(twoYearsBeforeToday.getMonth() + 1).toString().padStart(2, '0')}${twoYearsBeforeToday.getDate().toString().padStart(2, '0')}`;
        return purchaseYearMonthDay > twoYearsBeforeYearMonthDay; // i.e. '20220619' > '20220630' (YYYYMMDD)
    };

    return (
        <>
            <PropertyAddress
                propertyGuid={propertyGuid}
                readOnly={false}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={!isReview}>
                <br />
                <br />
                <div className='pageSubHeader'>Additional Information</div>
            </ShowIf>
            <YearBuiltRental
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <SiteBuiltOrManufacturedRental
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <AcquisitionFinancingRental
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={acquisitionToggle === false && acquisitionToggle !== undefined}>
                <CurrentDebtRental
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
                <ShowIf showIf={!currentDebt}>
                    <LoanProceedPropertyImprovementRental
                        propertyGuid={propertyGuid}
                        required={true}
                        isReview={isReview}
                    />
                </ShowIf>
                <DateOfPurchase
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
                <ShowIf showIf={isPurchaseDateLessThanTwoYears()}>
                    <PurchaseContractFile
                        propertyGuid={propertyGuid}
                        required={true}
                        isReview={isReview}
                    />
                    <CapitalExpendituresRental
                        propertyGuid={propertyGuid}
                        label='Have you done (or within six months will you do) more then $20,000 of capital expenditures?'
                        required={true}
                        isReview={isReview}
                    />
                    <ShowIf showIf={moreThan10kExpenditures === true}>
                        <CapitalExpendituresFile
                            propertyGuid={propertyGuid}
                            required={true}
                            isReview={isReview}
                        />
                    </ShowIf>
                </ShowIf>
                <ShowIf showIf={purchaseDate && !isPurchaseDateLessThanTwoYears()}>
                    <CapitalExpendituresRental
                        propertyGuid={propertyGuid}
                        label='Within 6 months after closing this requested loan, do you plan over $20,000 of capital expenditures?'
                        required={true}
                        isReview={isReview}
                    />
                    <ShowIf showIf={moreThan10kExpenditures === true}>
                        <CapitalExpendituresFile
                            propertyGuid={propertyGuid}
                            required={true}
                            isReview={isReview}
                        />
                    </ShowIf>
                </ShowIf>
            </ShowIf>
            <ShowIf showIf={acquisitionToggle === true}>
                <PurchaseContractFile
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
                <CapitalExpendituresRental
                    propertyGuid={propertyGuid}
                    label='In the first 6 months of ownership are over $20,000 of capital expenditures planned?'
                    required={true}
                    isReview={isReview}
                />
                <ShowIf showIf={moreThan10kExpenditures === true}>
                    <CapitalExpendituresFile
                        propertyGuid={propertyGuid}
                        required={true}
                        isReview={isReview}
                    />
                </ShowIf>
            </ShowIf>
            <ProvideLaterAdvisory
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <TargetLoanRequestRental
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <PreferredTerm
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <StructureAndRentalUnits
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            {structureType && (
                <RentalUnits
                    propertyGuid={propertyGuid}
                    header={'Unit Information'}
                    includeOccupied={true}
                    isReview={isReview}
                />
            )}
        </>
    );
};
